import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { Card } from 'components/Card';
import { Form } from 'components/HookForm';
import Message from 'components/Message';
import When from 'components/When';
import { openModal } from 'components/Modal/Modal.reducers';
import { updateSmartSpot as updateSmartSpotAction } from 'components/SmartSpot';
import { SMARTSPOT_MODE, SMARTSPOT_REASON } from 'constants/logic';
import { SMARTSPOT_UPDATE_SOURCE } from 'constants/tracking';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { trackSmartspotVenueLevelChange } from 'helpers/events';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectVenueSmartspotData, selectVenueSmartspotCanTurnOnOff, selectUserId } from 'selectors/general';

import {
  OPTIMIZELY_FEATURE_FLAGS_KEYS,
  PARTNER_DASHBOARD_SMARTSPOT_ONBOARDING,
} from 'components/Optimizely/Optimizely.features';
import { isFeatureEnabledForUser } from 'components/Optimizely/Optimizely.sdk';

import './SmartSpotOffCTA.scss';

const { block, element } = bem('smart-spot-off-cta');

const messages = defineMessages({
  title: {
    id: 'smart-tools.smart-spot-off-cta.title',
    defaultMessage: 'Protect your direct members while driving extra revenue',
  },
  description: {
    id: 'smart-tools.smart-spot-off-cta.description',
    defaultMessage:
      "SmartSpot automatically detects spots your direct members usually don't fill and helps turn them into extra revenue for your studio.",
  },
  turnOnButton: {
    id: 'smart-tools.smart-spot-off-cta.turn-on-button',
    defaultMessage: 'Turn SmartSpot ON',
  },
  exploreIntegrations: {
    id: 'smart-tools.smart-spot-off-cta.explore-integrations',
    defaultMessage: 'Explore our integrations',
  },
});

const FORM_NAME_ENABLE_SMART_SPOT = 'smartToolsV2EnableSmartSpot';

const SmartSpotOffCTA = ({ fitnessVenue, venueData, venueSmartspotDataIsLoaded, setShowOnboarding }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { venueName, venueSubtitle, isEnabled, isSmartspotEligible, venueId, isManualStudio } = venueData;

  const canManageSmartSpotOnOff = useSelector(state => selectVenueSmartspotCanTurnOnOff(state));

  const canTurnOnButton = venueSmartspotDataIsLoaded && isSmartspotEligible && !isEnabled && canManageSmartSpotOnOff;

  const canShowIntegrationsButton = isManualStudio && fitnessVenue(venueData) && !canTurnOnButton;

  const userId = useSelector(selectUserId);

  const {
    isEnabled: isSSEnabled,
    mode,
    minimumSpotsPeakTime,
    minimumSpotsOffpeakTime,
    allRemainingSpotsPeakTime,
    allRemainingSpotsOffpeakTime,
  } = useSelector(state => selectVenueSmartspotData(state));

  const initialValues = {
    isEnabled: isSSEnabled,
    mode: mode || SMARTSPOT_MODE.AGGRESSIVE,
    minimumSpotsPeakTime: minimumSpotsPeakTime || 0,
    minimumSpotsOffpeakTime: minimumSpotsOffpeakTime || 0,
    allRemainingSpotsPeakTime,
    allRemainingSpotsOffpeakTime,
  };

  const handleEnableSmartSpotSubmit = (formValues, formAsyncFns) => {
    const {
      isEnabled: formIsEnabled,
      mode: formMode,
      minimumSpotsPeakTime: formMinimumSpotsPeakTime,
      minimumSpotsOffpeakTime: formMinimumSpotsOffpeakTime,
      allRemainingSpotsPeakTime: formAllRemainingSpotsPeakTime,
      allRemainingSpotsOffpeakTime: formAllRemainingSpotsOffPeakTime,
    } = formValues;

    const values = {
      isEnabled: formIsEnabled,
      mode: formMode,
      minimumSpotsPeakTime: formMinimumSpotsPeakTime,
      minimumSpotsOffpeakTime: formMinimumSpotsOffpeakTime,
      allRemainingSpotsPeakTime: formAllRemainingSpotsPeakTime,
      allRemainingSpotsOffPeakTime: formAllRemainingSpotsOffPeakTime,
    };

    const meta = {
      venueId,
      venueName,
      venueSubtitle,
      reason: SMARTSPOT_REASON.VENUE_SETTING,
      source: SMARTSPOT_UPDATE_SOURCE.SMART_TOOLS,
    };

    trackSmartspotVenueLevelChange({ isEnabled: formIsEnabled, source: SMARTSPOT_UPDATE_SOURCE.SMART_TOOLS });
    braze.trackEvent(BRAZE_CUSTOM_EVENTS.SMARTSPOT_ON);
    return dispatch(updateSmartSpotAction(values, meta, formAsyncFns));
  };

  return (
    <Form
      id={FORM_NAME_ENABLE_SMART_SPOT}
      defaultValues={initialValues}
      isLoading={!venueSmartspotDataIsLoaded}
      onSubmit={handleEnableSmartSpotSubmit}>
      {function FormRender({ setValue, submit }) {
        const handleSubmitSmartSpotEnable = async () => {
          setValue('isEnabled', true);
          const isShowOnboardingFeatureEnabled = await isFeatureEnabledForUser(
            OPTIMIZELY_FEATURE_FLAGS_KEYS[PARTNER_DASHBOARD_SMARTSPOT_ONBOARDING],
            userId
          );
          if (isShowOnboardingFeatureEnabled) {
            setShowOnboarding({ showOnboardingReleaseExtraSpots: true, showOnboardingMinimumSpots: false });
          }

          submit();
        };

        return (
          <>
            <Card className={block()}>
              <div className={element('content')}>
                <div className={element('text-container')}>
                  <Message as="h2" className={element('title')} message={messages.title} />
                  <Message as="p" className={element('description')} message={messages.description} />
                  <When predicate={canShowIntegrationsButton}>
                    <Button
                      className={element('cta-button')}
                      styled="primary"
                      onClick={() => navigate(makePath.integrations())}>
                      <Message message={messages.exploreIntegrations} />
                    </Button>
                  </When>
                  <When predicate={canTurnOnButton}>
                    <Button
                      className={element('cta-button')}
                      styled="primary"
                      type={Button.TYPES.BUTTON}
                      onClick={() => {
                        dispatch(
                          openModal('SmartSpotONConfirmModal', {
                            onConfirm: handleSubmitSmartSpotEnable,
                          })
                        );
                      }}>
                      <Message message={messages.turnOnButton} />
                    </Button>
                  </When>
                </div>

                <div className={element('video-container')}>
                  <div className={element('video-embed')}>
                    <iframe
                      src="https://player.vimeo.com/video/1007792326?h=e38d3f2825&badge=0&autopause=0&player_id=0&app_id=58479"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      title="How ClassPass SmartTools Work – Become a Partner Today"
                    />
                    <script src="https://player.vimeo.com/api/player.js" />
                  </div>
                </div>
              </div>
            </Card>
          </>
        );
      }}
    </Form>
  );
};

SmartSpotOffCTA.propTypes = {
  fitnessVenue: PropTypes.func,
  venueData: PropTypes.shape({
    serviceTypes: PropTypes.arrayOf(PropTypes.string),
    isManualStudio: PropTypes.bool,
    countryCode: PropTypes.string,
    venueName: PropTypes.string,
    venueSubtitle: PropTypes.string,
    isEnabled: PropTypes.bool,
    isSmartspotEligible: PropTypes.bool,
    venueId: PropTypes.number,
  }),
  venueSmartspotDataIsLoaded: PropTypes.bool,
  isSmartspotEligible: PropTypes.bool,
  setShowOnboarding: PropTypes.func.isRequired,
};

export default SmartSpotOffCTA;
